import { lazy, type ElementType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { normalizeUrlPath } from "@/lib/urlNormalise";
import { RenderErrorBoundary } from "./components/RenderErrorBoundary";
import { LanguageRouter } from "./components/LanguageRouter";
import { ContactFormProvider } from "./components/ContactFormModal";
import Index from "./pages/Index";
import { RootDocument } from "./pages/RootDocument";
const AIDiscovery = lazy(() => import("./pages/services/AIDiscovery"));
const FixedPriceDelivery = lazy(() => import("./pages/services/FixedPriceDelivery"));
const EngineeringTeams = lazy(() => import("./pages/services/EngineeringTeams"));
const EngineeringEnablement = lazy(() => import("./pages/services/EngineeringEnablement"));
const KindlyWebSearch = lazy(() => import("./pages/open-source/KindlyWebSearch"));
const LadCodeReview = lazy(() => import("./pages/open-source/LadCodeReview"));
const TddSkill = lazy(() => import("./pages/open-source/TddSkill"));
const Insights = lazy(() => import("./pages/Insights"));
const SovereignAI = lazy(() => import("./pages/SovereignAI"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const Defence = lazy(() => import("./pages/industries/Defence"));
const Pharma = lazy(() => import("./pages/industries/Pharma"));
const Banking = lazy(() => import("./pages/industries/Banking"));
const Enterprise = lazy(() => import("./pages/industries/Enterprise"));
const Blog = lazy(() => import("./pages/Blog"));
const AiStrategyCompetitiveAdvantage = lazy(() => import("./pages/blog/AiStrategyCompetitiveAdvantage"));
const LackingDataFutureData = lazy(() => import("./pages/blog/LackingDataFutureData"));
const AiDevelopmentCulture = lazy(() => import("./pages/blog/AiDevelopmentCulture"));
const AiBusinessUseCase = lazy(() => import("./pages/blog/AiBusinessUseCase"));
const AiDesignsThatDontWork = lazy(() => import("./pages/blog/AiDesignsThatDontWork"));
const AiStrategiesSustainableAdvantage = lazy(() => import("./pages/blog/AiStrategiesSustainableAdvantage"));
const NetworkEffectTechProducts = lazy(() => import("./pages/blog/NetworkEffectTechProducts"));
const ChatGptCompetitiveAdvantage = lazy(() => import("./pages/blog/ChatGptCompetitiveAdvantage"));
const CompetentManagement = lazy(() => import("./pages/blog/CompetentManagement"));
const TangoTakesTwo = lazy(() => import("./pages/blog/TangoTakesTwo"));
const ClutchAwards2023 = lazy(() => import("./pages/blog/ClutchAwards2023"));
const Llama2Multilingual = lazy(() => import("./pages/blog/Llama2Multilingual"));
const MultilingualLlms = lazy(() => import("./pages/blog/MultilingualLlms"));
const GemmaMultilingualLlm = lazy(() => import("./pages/blog/GemmaMultilingualLlm"));
const LoraFineTuning = lazy(() => import("./pages/blog/LoraFineTuning"));
const UnslothFineTuning = lazy(() => import("./pages/blog/UnslothFineTuning"));
const SwiftFineTuning = lazy(() => import("./pages/blog/SwiftFineTuning"));
const ExploreAiForCompany = lazy(() => import("./pages/blog/ExploreAiForCompany"));
const Llama32Overview = lazy(() => import("./pages/blog/Llama32Overview"));
const BuyVsBuildAi = lazy(() => import("./pages/blog/BuyVsBuildAi"));
const TgiLlama33Inference = lazy(() => import("./pages/blog/TgiLlama33Inference"));
const VendorlandRecognition = lazy(() => import("./pages/blog/VendorlandRecognition"));
const ClaudeCodeQuality = lazy(() => import("./pages/blog/ClaudeCodeQuality"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

/**
 * Where a legacy blog URL lands, pinned to one locale.
 *
 * CWS-49 gave these two routes server-side rules in `public/_redirects`, and
 * Cloudflare cannot resolve a locale — its Pages redirects reference lists
 * "Redirect by country or language" as unsupported. The server therefore pins
 * `/en`, and a cold request never reaches the components below. They stay for
 * `vite dev` and `vite preview`, which do not process `_redirects`, and they
 * must land in the same place the server does: a URL that works when typed into
 * the address bar and goes somewhere else when reached from a link is the defect
 * `.github/review/rules/routing.md` names first.
 *
 * Bound to `src/lib/legacyRedirects.ts` by `App.redirects.test.tsx`. Not
 * imported from it: that module is policy and test-support, and nothing a
 * component can reach may import it.
 */
export const LEGACY_BLOG_REDIRECT_PATH = "/en/blog";

/**
 * Redirects legacy /post/<slug> paths to the pinned /en/blog/<slug>.
 *
 * Exported for the same reason {@link NewsletterRedirect} is: it is the
 * client-side half of a redirect whose server-side half lives in
 * `public/_redirects`, and `App.redirects.test.tsx` renders it to prove the two
 * land in the same place.
 */
export function LegacyPostRedirect() {
  const location = useLocation();
  const slug = decodeURIComponent(location.pathname.replace(/^\/post\//, ""));
  // Through `normalizeUrlPath`, not raw concatenation: the server rule answers
  // `/post/<slug>/` with the slashless destination, and without this the client
  // half would carry the visitor's trailing slash through to `/en/blog/<slug>/`
  // — a non-canonical URL, and the two halves disagreeing on the one input the
  // server explicitly has a second rule for.
  const destination = normalizeUrlPath(`${LEGACY_BLOG_REDIRECT_PATH}/${slug}`);
  return (
    <Navigate
      to={`${destination}${location.search}${location.hash}`}
      replace
    />
  );
}

export function NewsletterRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={`${LEGACY_BLOG_REDIRECT_PATH}${location.search}${location.hash}`}
      replace
    />
  );
}

function RoutePage({ component: Component }: { component: ElementType }) {
  // No Suspense here on purpose: LanguageRouter owns the single atomic
  // boundary around route content + FAQ + footer (CWS-179). A boundary here
  // would be the nearer one and would let the FAQ section and footer paint in
  // the shell before the lazy chunk arrives — the measured CLS 0.50 on every
  // lazy route.
  return <Component />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* CWS-237: the root document is the prerendered English homepage.
          The build writes the same component into dist/index.html, the boot
          pins `en` (initializeLanguage, CWS-232's hydrate branch), and the
          effect below resolves the visitor's language and replaces the URL
          with `/{lang}`. The en-as-default redirect is the owner decision
          recorded in the ticket; English visitors land on /en. */}
      <Route path="/" element={<RootDocument />} />

      {/* Legacy blog URL redirect — preserves SEO */}
      <Route path="/post/*" element={<LegacyPostRedirect />} />
      <Route path="/the-ai-compass-newsletter" element={<NewsletterRedirect />} />

      {/* Language-prefixed routes */}
      <Route element={<LanguageRouter />}>
        <Route path="/:lang">
          <Route index element={<Index />} />
          <Route path="services/consulting" element={<RoutePage component={AIDiscovery} />} />
          <Route path="services/fixed-price" element={<RoutePage component={FixedPriceDelivery} />} />
          <Route path="services/teams" element={<RoutePage component={EngineeringTeams} />} />
          <Route path="services/enablement" element={<RoutePage component={EngineeringEnablement} />} />
          <Route path="open-source/kindly-web-search" element={<RoutePage component={KindlyWebSearch} />} />
          <Route path="open-source/lad-code-review" element={<RoutePage component={LadCodeReview} />} />
          <Route path="open-source/tdd" element={<RoutePage component={TddSkill} />} />
          <Route path="sovereign-ai" element={<RoutePage component={SovereignAI} />} />
          <Route path="industries/defence" element={<RoutePage component={Defence} />} />
          <Route path="industries/pharma" element={<RoutePage component={Pharma} />} />
          <Route path="industries/finance" element={<RoutePage component={Banking} />} />
          <Route path="industries/enterprise" element={<RoutePage component={Enterprise} />} />
          <Route path="insights" element={<RoutePage component={Insights} />} />
          <Route path="case-studies" element={<RoutePage component={CaseStudies} />} />
          <Route path="case-studies/:slug" element={<RoutePage component={CaseStudyDetail} />} />
          <Route path="blog" element={<RoutePage component={Blog} />} />
          <Route path="blog/why-do-you-need-an-ai-strategy-ai-as-a-competitive-advantage" element={<RoutePage component={AiStrategyCompetitiveAdvantage} />} />
          <Route path="blog/lacking-data-for-machine-learning-models-build-for-the-future-data" element={<RoutePage component={LackingDataFutureData} />} />
          <Route path="blog/planning-for-a-new-ai-initiative-start-with-the-right-ai-development-culture" element={<RoutePage component={AiDevelopmentCulture} />} />
          <Route path="blog/what-is-your-ai-business-use-case-how-ai-can-help-and-harm-your-strategic-positioning" element={<RoutePage component={AiBusinessUseCase} />} />
          <Route path="blog/building-a-sustainable-ai-competitive-advantage-four-ai-designs-that-don-t-work" element={<RoutePage component={AiDesignsThatDontWork} />} />
          <Route path="blog/ai-strategies-that-offer-a-sustainable-competitive-advantage-principles-you-should-follow" element={<RoutePage component={AiStrategiesSustainableAdvantage} />} />
          <Route path="blog/all-you-need-is-a-network-effect-how-great-tech-and-ai-products-compete" element={<RoutePage component={NetworkEffectTechProducts} />} />
          <Route path="blog/can-chatgpt-build-a-competitive-advantage-for-your-company-and-if-so-how" element={<RoutePage component={ChatGptCompetitiveAdvantage} />} />
          <Route path="blog/competent-management-a-surprising-source-of-competitive-advantage" element={<RoutePage component={CompetentManagement} />} />
          <Route path="blog/the-tango-takes-two-building-synergy-between-ai-and-product-management" element={<RoutePage component={TangoTakesTwo} />} />
          <Route path="blog/shelpuk-ai-technology-consulting-honored-as-a-clutch-champion-and-clutch-global-for-2023" element={<RoutePage component={ClutchAwards2023} />} />
          <Route path="blog/llm-practitioner-s-guide-how-multilingual-llama-2-actually-is" element={<RoutePage component={Llama2Multilingual} />} />
          <Route path="blog/llm-practitioner-s-guide-how-multilingual-falcon-mistral-smaug-and-other-llms-are" element={<RoutePage component={MultilingualLlms} />} />
          <Route path="blog/llm-practitioner-s-guide-gemma-a-game-changing-multilingual-llm" element={<RoutePage component={GemmaMultilingualLlm} />} />
          <Route path="blog/low-rank-adaptation-lora-for-efficient-fine-tuning-of-large-language-models" element={<RoutePage component={LoraFineTuning} />} />
          <Route path="blog/how-to-fine-tune-llama-3-1-with-unsloth-twice-as-fast-and-using-70-less-gpu-memory" element={<RoutePage component={UnslothFineTuning} />} />
          <Route path="blog/fine-tuning-llama-3-1-with-swift-unsloth-alternative-for-multi-gpu-llm-training" element={<RoutePage component={SwiftFineTuning} />} />
          <Route path="blog/so-you-want-to-explore-ai-for-your-company-where-to-start" element={<RoutePage component={ExploreAiForCompany} />} />
          <Route path="blog/meet-llama-3-2-edge-llm-vision-agentic-capabilities-and-more" element={<RoutePage component={Llama32Overview} />} />
          <Route path="blog/buy-vs-build-in-ai-making-the-optimal-technology-choice" element={<RoutePage component={BuyVsBuildAi} />} />
          <Route path="blog/double-your-llama-3-3-inference-speed-with-huggingface-text-generation-inference" element={<RoutePage component={TgiLlama33Inference} />} />
          <Route path="blog/shelpuk-ai-technology-consulting-recognized-as-a-top-ai-consulting-company" element={<RoutePage component={VendorlandRecognition} />} />
          <Route path="blog/how-to-increase-claude-code-generation-quality-by-20-percent" element={<RoutePage component={ClaudeCodeQuality} />} />
          <Route path="terms" element={<RoutePage component={Terms} />} />
          <Route path="privacy" element={<RoutePage component={Privacy} />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<RoutePage component={NotFound} />} />
        </Route>
      </Route>
    </Routes>
  );
}

/**
 * The router-level boundary's fallback — deliberately plain English.
 *
 * This is the one surface whose own data may be the failure (the CWS-115
 * scope correction: a malformed ``faqs`` subtree in any locale bundle throws
 * inside ``LanguageRouter``'s FAQ resolution, before any of its JSX exists),
 * so the fallback depends on nothing that could be broken with it: no
 * i18next, no router hooks, no locale file — hardcoded English copy and a
 * plain anchor pinned to ``/en``. Exported for the same reason
 * {@link NewsletterRedirect} is: its dependency-freeness is a tested claim.
 */
export function RouterErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="mb-4 text-3xl font-bold">Something went wrong</h1>
        <p className="mb-6 text-muted-foreground">
          We could not load this page. Please return to the homepage and try
          again.
        </p>
        {/* Pinned /en, not /: an unprefixed / would re-run language
            resolution and land a visitor of the broken locale right back on
            the failure this fallback reports (the persisted/geo language
            decides, and the broken `faqs` subtree fails again at the FAQ
            resolution). English is the statically bundled floor (CWS-199),
            so /en renders for every visitor. */}
        <a href="/en" className="text-primary underline hover:text-primary/90">
          Return to the homepage
        </a>
      </div>
    </div>
  );
}

/**
 * The router-level boundary (CWS-5): the last line above the app.
 *
 * Sits inside ``BrowserRouter`` — above ``LanguageRouter``, below the
 * providers — and catches what the page-level boundary cannot: a throw from
 * ``LanguageRouter`` itself (the FAQ resolution ``useMemo`` runs before its
 * JSX exists) and from the header, footer or JSON-LD components. The
 * ``resetKey`` lets a browser Back out of a caught router failure recover
 * once the URL changes, mirroring the page-level rule.
 *
 * Exported (CWS-57) so the prerender entry can render the identical chrome
 * inside its ``StaticRouter``: hydration (RENDERING.md G-4) needs the server
 * and client trees to agree structurally, and a chrome-only drift sends
 * every page through React's full-client-render recovery — the outcome
 * `e2e/prerenderHydration.spec.ts` exists to catch.
 */
export function RouterErrorBoundary() {
  const location = useLocation();
  return (
    <RenderErrorBoundary
      surface="router"
      resetKey={location.pathname}
      fallback={<RouterErrorFallback />}
    >
      <AppRoutes />
    </RenderErrorBoundary>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ContactFormProvider>
      <Toaster />
      <BrowserRouter>
        <RouterErrorBoundary />
      </BrowserRouter>
    </ContactFormProvider>
  </QueryClientProvider>
);

export default App;
